/**
 * Module: screens/onboarding/steps/CityStep
 * Role: Buyer onboarding Step 2 — optional city chip + marketplace tile selectors.
 *       Both city and marketplaces are optional (L-259 override: CTA always enabled).
 *       ADR-023: no window.Telegram access, no platform literals.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - hooks/usePlatformBack:usePlatformBack
 *   - screens/onboarding/store:useOnboardingStore
 *   - screens/onboarding/constants:CITIES, MARKETPLACES
 *   - screens/flow/atoms/Frame, Caveat, CtaBar
 *   - screens/flow/atoms/Icons:Check
 *   - styles/tokens:P, T
 *
 * Used by: screens/onboarding/BuyerOnboardingScreen.tsx (route path="city")
 * Glossary: ai/glossary/buyer.md
 */

// ADR-023: no window.Telegram / platform literals outside src/platform/
// §7: no openExternal(BOT_LINK)

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../../../providers/PlatformProvider';
import { useOnboardingStore } from '../store';
import { CITIES, MARKETPLACES, type MarketplaceCode } from '../constants';
import { Caveat } from '../../flow/atoms/Caveat';
import { PressBtn } from '../../../components/atoms/PressBtn';
import { Check } from '../../flow/atoms/Icons';
import { P, T } from '../../../styles/tokens';

const MARKETPLACE_LABELS: Record<MarketplaceCode, string> = {
  wb:   'Wildberries',
  ozon: 'Ozon',
  ym:   'Я.Маркет',
};

export function CityStep() {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { city, marketplaces, setCity, toggleMarketplace } = useOnboardingStore();

  // Back → previous step (welcome)
  useEffect(() => {
    platform.showBackButton(() => navigate('../welcome'));
    return () => { platform.hideBackButton(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCity(c: string) {
    // Toggle-off if same city clicked again
    setCity(city === c ? null : c);
  }

  function handleNext() {
    navigate('../preparing');
  }

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
      background: P.bg, overflow: 'hidden' }}>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'],
        padding: '16px 16px 120px' }}>

        {/* Heading */}
        <div style={{ padding: '4px 8px 0' }}>
          <Caveat color={P.jade} rot={-2} size={20}>под вас</Caveat>
          <div style={{ fontFamily: T.fontDisplay, fontSize: 24, fontWeight: 700,
            letterSpacing: -0.5, lineHeight: 1.22, color: P.ink, marginTop: 5 }}>
            Откуда вы и где покупаете?
          </div>
          <div style={{ fontSize: 13, color: P.inkSoft, marginTop: 8, lineHeight: 1.5 }}>
            Покажем находки с доставкой в ваш город и от удобных маркетплейсов.
          </div>
        </div>

        {/* City chips */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11, color: P.inkSoft, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Город
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {CITIES.map((c) => {
              const active = c === city;
              return (
                <button key={c} onClick={() => handleCity(c)} style={{
                  background: active ? P.ink : '#fff',
                  color: active ? '#fff' : P.ink,
                  border: `1px solid ${active ? P.ink : P.line}`,
                  borderRadius: 999, padding: '9px 16px',
                  fontFamily: T.fontBody, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.15s ease',
                }}>{c}</button>
              );
            })}
          </div>
        </div>

        {/* Marketplace tiles */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11, color: P.inkSoft, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
            Маркетплейсы
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MARKETPLACES.map((m) => {
              const active = marketplaces.includes(m);
              return (
                <button key={m} onClick={() => toggleMarketplace(m)} style={{
                  background: active ? P.terraSoft : '#fff',
                  border: `1.5px solid ${active ? P.terracotta : P.line}`,
                  borderRadius: 14, padding: '13px 14px',
                  display: 'flex', alignItems: 'center', gap: 12,
                  cursor: 'pointer', textAlign: 'left',
                  transition: 'all 0.15s ease',
                }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: 6,
                    background: active ? P.terracotta : '#fff',
                    border: `1.5px solid ${active ? P.terracotta : P.line}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, color: '#fff',
                    transition: 'all 0.15s ease',
                  }}>
                    {active && <Check size={12} />}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: P.ink, flex: 1 }}>
                    {MARKETPLACE_LABELS[m]}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Fixed CTA */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '14px 16px 30px',
        background: `linear-gradient(to top, ${P.bg} 60%, rgba(242,239,230,0))`,
      }}>
        <PressBtn onClick={handleNext} bg={P.terracotta}>
          Далее
        </PressBtn>
      </div>
    </div>
  );
}
